import { useEffect, useMemo, useRef, useState } from 'react'
import {
  APPSUITE_ACTIVE_CLIENT_CHANGED_EVENT,
  listAppSuiteUserClients,
  listAppSuiteUsersByClient,
  createAppSuiteUserInvitation,
  getAppSuiteUserQuota,
  getAppSuiteActiveClientId,
  setAppSuiteActiveClientId,
  listAppSuiteAvailableProjectIds,
  listAppSuiteUserInvitations,
  removeAppSuiteUser,
  revokeAppSuiteInvitation,
  listClientApps,
  registerClientApp,
  deleteClientApp,
  lookupClientApps,
  connectFirebaseApp,
  discoverAccessibleApps,
  type AppSuiteIAMClient,
  type AppSuiteIAMDeveloper,
  type AppSuiteIAMQuota,
  type AppSuiteAvailableProjectId,
  type AppSuiteUserInvitation,
  type AppSuiteClientApp,
} from '../services/appsuiteApi'
import { initializeApp, getApps, getApp as getFirebaseApp } from 'firebase/app'
import { getAuth, GoogleAuthProvider, signInWithPopup } from 'firebase/auth'
import { auth } from '../../ultron-turing-manager/src/services/firebase'
import './AppSuiteUsersPage.css'

/** Lazily initialise the cms-doraemon Firebase app (UltronCloud auth) */
function getUltronCloudAuth() {
  const existing = getApps().find((a) => a.name === 'ultroncloud')
  if (existing) return getAuth(getFirebaseApp('ultroncloud'))
  const app = initializeApp({
    apiKey: 'AIzaSyBXU_bKn541LAEfF55LABXS9-FafpD650E',
    authDomain: 'cms-doraemon.firebaseapp.com',
    projectId: 'cms-doraemon',
    storageBucket: 'cms-doraemon.appspot.com',
    messagingSenderId: '74002342753',
    appId: '1:74002342753:web:8996f0659f57ff7fed5b20',
    databaseURL: 'https://cms-doraemon.firebaseio.com',
  }, 'ultroncloud')
  return getAuth(app)
}

function Avatar({ name, email }: { name?: string | null; email?: string | null }) {
  const initials = (name || email || '?')
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('')
  return <span className="iam-avatar">{initials || '?'}</span>
}

function RoleBadge({ role }: { role?: string | null }) {
  const isAdmin = role === 'client_admin'
  return (
    <span className={`iam-badge iam-badge--${isAdmin ? 'admin' : 'dev'}`}>
      {isAdmin ? 'Admin' : 'Developer'}
    </span>
  )
}

function StatusDot({ status }: { status?: string | null }) {
  const s = (status || '').toLowerCase()
  return <span className={`iam-status-dot iam-status-dot--${s}`} title={s} />
}

function InviteStatusBadge({ invitation }: { invitation: AppSuiteUserInvitation }) {
  const expired = invitation.expiresAt ? new Date(invitation.expiresAt) < new Date() : false
  const revoked = invitation.status === 'revoked'
  const accepted = invitation.status === 'accepted'
  if (accepted) return <span className="iam-badge iam-badge--accepted">Accepted</span>
  if (revoked) return <span className="iam-badge iam-badge--revoked">Revoked</span>
  if (expired) return <span className="iam-badge iam-badge--expired">Expired</span>
  return <span className="iam-badge iam-badge--pending">Pending</span>
}

function fmtDate(iso?: string | null) {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString('zh-TW', { month: 'short', day: 'numeric', year: 'numeric' })
}

export default function AppSuiteUsersPage() {
  const [clients, setClients] = useState<AppSuiteIAMClient[]>([])
  const [selectedClientId, setSelectedClientId] = useState(() => getAppSuiteActiveClientId())
  const [users, setUsers] = useState<AppSuiteIAMDeveloper[]>([])
  const [invitations, setInvitations] = useState<AppSuiteUserInvitation[]>([])
  const [quota, setQuota] = useState<AppSuiteIAMQuota | null>(null)
  const [projectQuota, setProjectQuota] = useState<AppSuiteAvailableProjectId | null>(null)
  const [showInvitePanel, setShowInvitePanel] = useState(false)
  const [inviteEmail, setInviteEmail] = useState('')
  const [inviteName, setInviteName] = useState('')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [removingId, setRemovingId] = useState<string | null>(null)
  const [message, setMessage] = useState<{ text: string; type: 'success' | 'error' } | null>(null)
  const loadDetailsRequestRef = useRef(0)

  const selectedClient = useMemo(
    () => clients.find((c) => c.id === selectedClientId) || null,
    [clients, selectedClientId]
  )

  const notify = (text: string, type: 'success' | 'error' = 'success') => {
    setMessage({ text, type })
    window.setTimeout(() => setMessage(null), 3000)
  }

  const filterClientsByCurrentUser = async (list: AppSuiteIAMClient[]): Promise<AppSuiteIAMClient[]> => {
    const currentEmail = String(auth?.currentUser?.email || '').trim().toLowerCase()
    const currentUserId = String(auth?.currentUser?.uid || '').trim()
    if (!currentEmail && !currentUserId) return []
    const checks = await Promise.allSettled(
      list.map(async (client) => {
        const usersInClient = await listAppSuiteUsersByClient(client.id)
        const hasAccess = usersInClient.some((user) => {
          if (String(user.status || '').toLowerCase() !== 'active') return false
          return (
            (Boolean(currentEmail) && String(user.email || '').trim().toLowerCase() === currentEmail) ||
            (Boolean(currentUserId) && String(user.userId || '').trim() === currentUserId)
          )
        })
        return hasAccess ? client : null
      })
    )
    return checks
      .filter((e): e is PromiseFulfilledResult<AppSuiteIAMClient | null> => e.status === 'fulfilled')
      .map((e) => e.value)
      .filter((c): c is AppSuiteIAMClient => Boolean(c))
  }

  const loadClients = async () => {
    setLoading(true)
    try {
      const list = await listAppSuiteUserClients()
      const scoped = await filterClientsByCurrentUser(list)
      setClients(scoped)
      const cached = getAppSuiteActiveClientId()
      if ((!selectedClientId || !scoped.some((c) => c.id === selectedClientId)) && scoped.length > 0) {
        const next = scoped.find((c) => c.id === cached)?.id || scoped[0].id
        setSelectedClientId(next)
        setAppSuiteActiveClientId(next)
      } else if (scoped.length === 0) {
        setSelectedClientId('')
        setAppSuiteActiveClientId('')
      }
    } catch (e) {
      notify(e instanceof Error ? e.message : '載入失敗', 'error')
    } finally {
      setLoading(false)
    }
  }

  const loadDetails = async (clientId: string) => {
    if (!clientId) return
    const requestId = ++loadDetailsRequestRef.current
    setLoading(true)
    setUsers([])
    setInvitations([])
    setQuota(null)
    setProjectQuota(null)
    try {
      const [usersR, invitesR, quotaR, pqR] = await Promise.allSettled([
        listAppSuiteUsersByClient(clientId),
        listAppSuiteUserInvitations(clientId),
        getAppSuiteUserQuota(clientId),
        listAppSuiteAvailableProjectIds(),
      ])
      if (requestId !== loadDetailsRequestRef.current) return
      setUsers(usersR.status === 'fulfilled' ? usersR.value : [])
      setInvitations(invitesR.status === 'fulfilled' ? invitesR.value : [])
      setQuota(quotaR.status === 'fulfilled' ? quotaR.value : null)
      setProjectQuota(
        pqR.status === 'fulfilled' ? (pqR.value.find((i) => i.clientId === clientId) ?? null) : null
      )
    } catch (_e) {
      if (requestId !== loadDetailsRequestRef.current) return
      setUsers([])
      setInvitations([])
      setQuota(null)
      setProjectQuota(null)
      notify('載入失敗', 'error')
    } finally {
      if (requestId === loadDetailsRequestRef.current) setLoading(false)
    }
  }

  useEffect(() => { void loadClients() }, []) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!selectedClientId) return
    setAppSuiteActiveClientId(selectedClientId)
    void loadDetails(selectedClientId)
  }, [selectedClientId]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const handler = (event: Event) => {
      const next = String((event as CustomEvent<{ clientId?: string }>).detail?.clientId || getAppSuiteActiveClientId()).trim()
      if (next !== selectedClientId) setSelectedClientId(next)
    }
    window.addEventListener(APPSUITE_ACTIVE_CLIENT_CHANGED_EVENT, handler as EventListener)
    return () => window.removeEventListener(APPSUITE_ACTIVE_CLIENT_CHANGED_EVENT, handler as EventListener)
  }, [selectedClientId])

  const handleInvite = async () => {
    if (!selectedClientId || !inviteEmail.trim()) return
    setSaving(true)
    try {
      const result = await createAppSuiteUserInvitation(selectedClientId, {
        email: inviteEmail.trim(),
        name: inviteName.trim() || undefined,
        role: 'client_developer',
      })
      if (!result.success) throw new Error(result.error || 'Invite failed')
      setInviteEmail('')
      setInviteName('')
      setShowInvitePanel(false)
      await loadDetails(selectedClientId)
      notify(result.emailSent ? '邀請已送出' : '邀請已建立（Email 未發送）')
    } catch (e) {
      notify(e instanceof Error ? e.message : 'Invite failed', 'error')
    } finally {
      setSaving(false)
    }
  }

  const handleRemoveUser = async (user: AppSuiteIAMDeveloper) => {
    if (!selectedClientId || !window.confirm(`確認移除 ${user.name || user.email}？`)) return
    setRemovingId(user.id)
    try {
      const result = await removeAppSuiteUser(selectedClientId, user.id)
      if (!result.success) throw new Error(result.error || 'Remove failed')
      await loadDetails(selectedClientId)
      notify('已移除成員')
    } catch (e) {
      notify(e instanceof Error ? e.message : 'Remove failed', 'error')
    } finally {
      setRemovingId(null)
    }
  }

  const handleRevokeInvitation = async (inv: AppSuiteUserInvitation) => {
    if (!selectedClientId || !window.confirm(`確認撤銷 ${inv.email} 的邀請？`)) return
    setRemovingId(inv.id)
    try {
      const result = await revokeAppSuiteInvitation(selectedClientId, inv.id)
      if (!result.success) throw new Error(result.error || 'Revoke failed')
      await loadDetails(selectedClientId)
      notify('邀請已撤銷')
    } catch (e) {
      notify(e instanceof Error ? e.message : 'Revoke failed', 'error')
    } finally {
      setRemovingId(null)
    }
  }

  // ── Client Apps ──────────────────────────────────────────────────────────
  const [clientApps, setClientApps] = useState<AppSuiteClientApp[]>([])
  const [showAppPanel, setShowAppPanel] = useState(false)
  const [registerMode, setRegisterMode] = useState<'manual' | 'firebase'>('firebase')
  // Manual mode (for admins who have an API key)
  const [newAppApiKey, setNewAppApiKey] = useState('')
  const [newAppId, setNewAppId] = useState('')
  const [newAppName, setNewAppName] = useState('')
  const [lookupLoading, setLookupLoading] = useState(false)
  const [lookupResults, setLookupResults] = useState<{ appId: string; name: string }[] | null>(null)
  // Firebase session mode
  const [firebaseAppName, setFirebaseAppName] = useState('')
  const [firebaseDiscovering, setFirebaseDiscovering] = useState(false)
  const [firebaseIdToken, setFirebaseIdToken] = useState('')
  const [firebaseRefreshToken, setFirebaseRefreshToken] = useState('')
  const [firebaseUid, setFirebaseUid] = useState('')
  const [firebaseEmail, setFirebaseEmail] = useState('')
  const [firebaseDiscoveredApps, setFirebaseDiscoveredApps] = useState<{ appId: string; name: string }[]>([])
  const [firebaseSelectedAppId, setFirebaseSelectedAppId] = useState('')

  const loadClientApps = async (clientId: string) => {
    if (!clientId) return
    try { setClientApps(await listClientApps(clientId)) }
    catch (_e) { setClientApps([]) }
  }

  useEffect(() => {
    if (!selectedClientId) { setClientApps([]); return }
    void loadClientApps(selectedClientId)
    setShowAppPanel(false); setLookupResults(null)
    setNewAppApiKey(''); setNewAppId(''); setNewAppName('')
    setFirebaseAppName(''); setFirebaseIdToken(''); setFirebaseRefreshToken('')
    setFirebaseUid(''); setFirebaseEmail('')
    setFirebaseDiscoveredApps([]); setFirebaseSelectedAppId('')
  }, [selectedClientId]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleLookupApps = async () => {
    if (!selectedClientId || !newAppApiKey.trim() || !newAppId.trim()) {
      notify('API Key 和 App ID 必填', 'error'); return
    }
    setLookupLoading(true); setLookupResults(null)
    try {
      const results = await lookupClientApps(selectedClientId, { apiKey: newAppApiKey.trim(), appId: newAppId.trim() })
      setLookupResults(results)
      if (results.length === 1) { setNewAppId(results[0].appId); setNewAppName(results[0].name) }
    } catch (e) {
      notify(e instanceof Error ? e.message : 'Lookup failed', 'error')
    } finally { setLookupLoading(false) }
  }

  const handleRegisterApp = async () => {
    if (!selectedClientId || !newAppApiKey.trim() || !newAppId.trim()) {
      notify('API Key 和 App ID 必填', 'error'); return
    }
    setSaving(true)
    try {
      await registerClientApp(selectedClientId, { appId: newAppId.trim(), apiKey: newAppApiKey.trim(), name: newAppName.trim() || newAppId.trim() })
      setNewAppApiKey(''); setNewAppId(''); setNewAppName(''); setLookupResults(null); setShowAppPanel(false)
      await loadClientApps(selectedClientId)
      notify('App 已登記')
    } catch (e) {
      notify(e instanceof Error ? e.message : 'Register failed', 'error')
    } finally { setSaving(false) }
  }

  const handleFirebaseDiscover = async () => {
    if (!selectedClientId) return
    setFirebaseDiscovering(true)
    setFirebaseDiscoveredApps([]); setFirebaseSelectedAppId('')
    try {
      const ultronAuth = getUltronCloudAuth()
      const provider = new GoogleAuthProvider()
      const result = await signInWithPopup(ultronAuth, provider)
      const idToken = await result.user.getIdToken()
      const refreshToken = result.user.refreshToken || ''
      setFirebaseIdToken(idToken)
      setFirebaseRefreshToken(refreshToken)
      setFirebaseUid(result.user.uid)
      setFirebaseEmail(result.user.email || '')
      const apps = await discoverAccessibleApps(selectedClientId, idToken)
      setFirebaseDiscoveredApps(apps)
      if (apps.length === 1) setFirebaseSelectedAppId(apps[0].appId)
      if (apps.length === 0) notify('此帳號沒有可用的 App', 'error')
    } catch (e) {
      if ((e as { code?: string }).code === 'auth/popup-closed-by-user') return
      notify(e instanceof Error ? e.message : 'Discovery failed', 'error')
    } finally { setFirebaseDiscovering(false) }
  }

  const handleFirebaseRegister = async () => {
    if (!selectedClientId || !firebaseIdToken || !firebaseRefreshToken || !firebaseSelectedAppId) {
      notify('請選擇 App', 'error'); return
    }
    setSaving(true)
    try {
      const selectedApp = firebaseDiscoveredApps.find((a) => a.appId === firebaseSelectedAppId)
      await connectFirebaseApp(selectedClientId, {
        idToken: firebaseIdToken,
        refreshToken: firebaseRefreshToken,
        appId: firebaseSelectedAppId,
        name: firebaseAppName.trim() || selectedApp?.name || firebaseSelectedAppId,
        uid: firebaseUid || undefined,
        email: firebaseEmail || undefined,
      })
      setFirebaseIdToken(''); setFirebaseRefreshToken('')
      setFirebaseUid(''); setFirebaseEmail('')
      setFirebaseDiscoveredApps([]); setFirebaseSelectedAppId('')
      setFirebaseAppName(''); setShowAppPanel(false)
      await loadClientApps(selectedClientId)
      notify('App 已透過目前帳號登記')
    } catch (e) {
      notify(e instanceof Error ? e.message : 'Register failed', 'error')
    } finally { setSaving(false) }
  }

  const handleDeleteApp = async (app: AppSuiteClientApp) => {
    if (!selectedClientId || !window.confirm(`確認移除 ${app.name} (${app.appId})？`)) return
    setRemovingId(app.id)
    try {
      await deleteClientApp(selectedClientId, app.id)
      await loadClientApps(selectedClientId)
      notify('App 已移除')
    } catch (e) {
      notify(e instanceof Error ? e.message : 'Remove failed', 'error')
    } finally { setRemovingId(null) }
  }

  const appSuiteUsers = users.filter((u) => u.role !== 'client_admin')
  const adminUsers = users.filter((u) => u.role === 'client_admin')
  const pendingInvitations = invitations.filter((i) => i.status === 'pending' && !(i.expiresAt && new Date(i.expiresAt) < new Date()))

  return (
    <div className="iam-page appfactory-page-content">
      {message ? (
        <div className={`iam-toast iam-toast--${message.type}`}>{message.text}</div>
      ) : null}

      <div className="iam-page-header">
        <div>
          <h2 className="iam-page-title">Members</h2>
          <p className="iam-page-sub">
            {selectedClient ? selectedClient.name || selectedClient.id : '—'} · App Suite 用戶管理
          </p>
        </div>
        <button className="iam-btn iam-btn--ghost" onClick={() => void loadClients()} disabled={loading}>
          <span className="material-symbols-rounded">refresh</span>
        </button>
      </div>

      <div className="iam-stats-row">
        <div className="iam-stat">
          <span className="iam-stat-value">{appSuiteUsers.filter((u) => u.status === 'active').length}</span>
          <span className="iam-stat-label">Active Members</span>
        </div>
        <div className="iam-stat">
          <span className="iam-stat-value">{pendingInvitations.length}</span>
          <span className="iam-stat-label">Pending Invites</span>
        </div>
        <div className="iam-stat">
          <span className="iam-stat-value">{quota?.maxUsers ?? '—'}</span>
          <span className="iam-stat-label">User Quota</span>
        </div>
        <div className="iam-stat">
          <span className="iam-stat-value">{projectQuota?.projectQuota ?? '—'}</span>
          <span className="iam-stat-label">Project Quota</span>
        </div>
        <div className="iam-stat">
          <span className="iam-stat-value">{projectQuota?.remainingProjects ?? '—'}</span>
          <span className="iam-stat-label">Projects Remaining</span>
        </div>
      </div>

      {loading ? <div className="iam-loading"><span className="material-symbols-rounded iam-spin">progress_activity</span></div> : null}

      {/* Members table */}
      <div className="iam-card">
        <div className="iam-card-header">
          <span className="iam-card-title">Members</span>
          <button
            className="iam-btn iam-btn--primary"
            disabled={!selectedClientId || saving}
            onClick={() => setShowInvitePanel((v) => !v)}
          >
            <span className="material-symbols-rounded">person_add</span>
            Invite
          </button>
        </div>

        {showInvitePanel ? (
          <div className="iam-invite-panel">
            <div className="iam-invite-fields">
              <input
                className="iam-input"
                placeholder="Email address"
                type="email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') void handleInvite() }}
              />
              <input
                className="iam-input"
                placeholder="Name (optional)"
                value={inviteName}
                onChange={(e) => setInviteName(e.target.value)}
              />
            </div>
            <div className="iam-invite-actions">
              <span className="iam-invite-role-note">Role: Developer · Admin accounts are managed via AppFactory</span>
              <button className="iam-btn iam-btn--ghost" onClick={() => { setShowInvitePanel(false); setInviteEmail(''); setInviteName('') }}>Cancel</button>
              <button className="iam-btn iam-btn--primary" disabled={saving || !inviteEmail.trim()} onClick={() => void handleInvite()}>
                {saving ? 'Sending…' : 'Send Invite'}
              </button>
            </div>
          </div>
        ) : null}

        <table className="iam-table">
          <thead>
            <tr>
              <th>Member</th>
              <th style={{ width: 120 }}>Role</th>
              <th style={{ width: 110 }}>Status</th>
              <th style={{ width: 100 }}>Source</th>
              <th style={{ width: 48 }} />
            </tr>
          </thead>
          <tbody>
            {appSuiteUsers.map((user) => (
              <tr key={user.id}>
                <td>
                  <div className="iam-member-cell">
                    <Avatar name={user.name} email={user.email} />
                    <div>
                      <div className="iam-member-name">{user.name || '—'}</div>
                      <div className="iam-member-email">{user.email}</div>
                    </div>
                  </div>
                </td>
                <td><RoleBadge role={user.role} /></td>
                <td><div className="iam-status-row"><StatusDot status={user.status} /><span className="iam-status-text">{user.status || '—'}</span></div></td>
                <td><span className="iam-source">App Suite</span></td>
                <td>
                  <button
                    className="iam-btn-icon iam-btn-icon--danger"
                    title="Remove"
                    disabled={removingId === user.id}
                    onClick={() => void handleRemoveUser(user)}
                  >
                    <span className="material-symbols-rounded">person_remove</span>
                  </button>
                </td>
              </tr>
            ))}
            {adminUsers.map((user) => (
              <tr key={user.id} className="iam-row--admin">
                <td>
                  <div className="iam-member-cell">
                    <Avatar name={user.name} email={user.email} />
                    <div>
                      <div className="iam-member-name">{user.name || '—'}</div>
                      <div className="iam-member-email">{user.email}</div>
                    </div>
                  </div>
                </td>
                <td><RoleBadge role={user.role} /></td>
                <td><div className="iam-status-row"><StatusDot status={user.status} /><span className="iam-status-text">{user.status || '—'}</span></div></td>
                <td><span className="iam-source iam-source--factory">AppFactory</span></td>
                <td />
              </tr>
            ))}
            {users.length === 0 && !loading ? (
              <tr><td colSpan={5} className="iam-empty">No members yet</td></tr>
            ) : null}
          </tbody>
        </table>
      </div>

      {/* Client Apps */}
      <div className="iam-card">
        <div className="iam-card-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span className="iam-card-title">Client Apps</span>
            {clientApps.length > 0 && <span className="iam-card-count">{clientApps.length}</span>}
          </div>
          <button
            className="iam-btn iam-btn--primary"
            disabled={!selectedClientId || saving}
            onClick={() => setShowAppPanel((v) => !v)}
          >
            <span className="material-symbols-rounded">add</span>
            Register App
          </button>
        </div>

        {showAppPanel ? (
          <div className="iam-invite-panel">
            {/* Mode tabs */}
            <div style={{ display: 'flex', gap: 0, marginBottom: 12, borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
              {(['firebase', 'manual'] as const).map((mode) => (
                <button
                  key={mode}
                  type="button"
                  onClick={() => setRegisterMode(mode)}
                  style={{
                    background: 'none', border: 'none', cursor: 'pointer', padding: '6px 16px',
                    fontSize: 13, fontWeight: registerMode === mode ? 600 : 400,
                    color: registerMode === mode ? '#e2e8f0' : '#64748b',
                    borderBottom: registerMode === mode ? '2px solid #3b82f6' : '2px solid transparent',
                    marginBottom: -1,
                  }}
                >
                  {mode === 'firebase' ? '目前帳號' : '手動輸入'}
                </button>
              ))}
            </div>

            {registerMode === 'firebase' ? (
              /* Firebase session mode */
              <>
                <div className="iam-invite-fields">
                  {firebaseDiscovering ? (
                    <div style={{ color: '#94a3b8', fontSize: 13, padding: '8px 0' }}>
                      <span className="material-symbols-rounded iam-spin" style={{ fontSize: 16, verticalAlign: 'middle', marginRight: 6 }}>progress_activity</span>
                      探索可用 Apps…
                    </div>
                  ) : firebaseDiscoveredApps.length > 0 ? (
                    <select
                      className="iam-input"
                      value={firebaseSelectedAppId}
                      onChange={(e) => setFirebaseSelectedAppId(e.target.value)}
                    >
                      <option value="">— 選擇要登記的 App —</option>
                      {firebaseDiscoveredApps.map((a) => (
                        <option key={a.appId} value={a.appId}>{a.name} ({a.appId})</option>
                      ))}
                    </select>
                  ) : null}
                  <input
                    className="iam-input"
                    placeholder="Name (optional)"
                    value={firebaseAppName}
                    onChange={(e) => setFirebaseAppName(e.target.value)}
                  />
                </div>
                <div className="iam-invite-actions">
                  <span className="iam-invite-role-note">
                    {firebaseDiscoveredApps.length > 0
                      ? '選擇 App 後按 Register 完成登記'
                      : '使用目前登入的帳號自動探索可用 Apps'}
                  </span>
                  <button className="iam-btn iam-btn--ghost" onClick={() => { setShowAppPanel(false); setFirebaseDiscoveredApps([]); setFirebaseSelectedAppId(''); setFirebaseAppName('') }}>Cancel</button>
                  {firebaseDiscoveredApps.length === 0 ? (
                    <button
                      className="iam-btn iam-btn--primary"
                      disabled={firebaseDiscovering || !selectedClientId}
                      onClick={() => void handleFirebaseDiscover()}
                    >
                      {firebaseDiscovering ? 'Discovering…' : '探索可用 Apps'}
                    </button>
                  ) : (
                    <button
                      className="iam-btn iam-btn--primary"
                      disabled={saving || !firebaseSelectedAppId}
                      onClick={() => void handleFirebaseRegister()}
                    >
                      {saving ? 'Saving…' : 'Register'}
                    </button>
                  )}
                </div>
              </>
            ) : (
              /* Manual API key mode */
              <>
                <div className="iam-invite-fields">
                  <input
                    className="iam-input"
                    placeholder="UltronCloud API Key"
                    type="password"
                    value={newAppApiKey}
                    onChange={(e) => { setNewAppApiKey(e.target.value); setLookupResults(null) }}
                  />
                  {lookupResults && lookupResults.length > 0 ? (
                    <select
                      className="iam-input"
                      value={newAppId}
                      onChange={(e) => {
                        setNewAppId(e.target.value)
                        const found = lookupResults.find((r) => r.appId === e.target.value)
                        if (found) setNewAppName(found.name)
                      }}
                    >
                      <option value="">— Select App —</option>
                      {lookupResults.map((r) => (
                        <option key={r.appId} value={r.appId}>{r.name} ({r.appId})</option>
                      ))}
                    </select>
                  ) : (
                    <input
                      className="iam-input"
                      placeholder="App ID (e.g. KLMGeDMy)"
                      value={newAppId}
                      onChange={(e) => setNewAppId(e.target.value)}
                    />
                  )}
                  <input
                    className="iam-input"
                    placeholder="Name (optional)"
                    value={newAppName}
                    onChange={(e) => setNewAppName(e.target.value)}
                  />
                </div>
                <div className="iam-invite-actions">
                  <span className="iam-invite-role-note">
                    {lookupResults !== null && lookupResults.length === 0
                      ? 'No accessible apps found for this key'
                      : 'Enter API Key + App ID, or Lookup to select from available apps'}
                  </span>
                  <button className="iam-btn iam-btn--ghost" onClick={() => { setShowAppPanel(false); setNewAppApiKey(''); setNewAppId(''); setNewAppName(''); setLookupResults(null) }}>Cancel</button>
                  <button className="iam-btn iam-btn--ghost" disabled={lookupLoading || !newAppApiKey.trim() || !newAppId.trim()} onClick={() => void handleLookupApps()}>
                    {lookupLoading ? 'Looking up…' : 'Lookup'}
                  </button>
                  <button className="iam-btn iam-btn--primary" disabled={saving || !newAppApiKey.trim() || !newAppId.trim()} onClick={() => void handleRegisterApp()}>
                    {saving ? 'Saving…' : 'Register'}
                  </button>
                </div>
              </>
            )}
          </div>
        ) : null}

        <table className="iam-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>App ID</th>
              <th>Auth</th>
              <th>Registered</th>
              <th style={{ width: 48 }} />
            </tr>
          </thead>
          <tbody>
            {clientApps.map((app) => (
              <tr key={app.id}>
                <td>{app.name}</td>
                <td><code style={{ fontSize: 12 }}>{app.appId}</code></td>
                <td><span style={{ fontSize: 12, opacity: 0.6 }}>{app.authType === 'firebase' ? '🔑 Firebase' : app.maskedApiKey ? `****${String(app.maskedApiKey).slice(-4)}` : '—'}</span></td>
                <td className="iam-date">{fmtDate(app.createdAt)}</td>
                <td>
                  <button
                    className="iam-btn-icon iam-btn-icon--danger"
                    title="Remove"
                    disabled={removingId === app.id}
                    onClick={() => void handleDeleteApp(app)}
                  >
                    <span className="material-symbols-rounded">delete</span>
                  </button>
                </td>
              </tr>
            ))}
            {clientApps.length === 0 && !loading ? (
              <tr><td colSpan={5} className="iam-empty">尚未登記任何 App — 按 Register App 新增</td></tr>
            ) : null}
          </tbody>
        </table>
      </div>

      {/* Pending invitations */}
      {invitations.length > 0 ? (
        <div className="iam-card">
          <div className="iam-card-header">
            <span className="iam-card-title">Invitations</span>
            <span className="iam-card-count">{invitations.length}</span>
          </div>
          <table className="iam-table">
            <thead>
              <tr>
                <th>Email</th>
                <th>Name</th>
                <th>Sent</th>
                <th>Expires</th>
                <th>Status</th>
                <th style={{ width: 60 }} />
              </tr>
            </thead>
            <tbody>
              {invitations.map((inv) => {
                const canRevoke = inv.status === 'pending' && !(inv.expiresAt && new Date(inv.expiresAt) < new Date())
                return (
                  <tr key={inv.id}>
                    <td className="iam-member-email">{inv.email}</td>
                    <td>{inv.name || '—'}</td>
                    <td className="iam-date">{fmtDate(inv.invitedAt)}</td>
                    <td className="iam-date">{fmtDate(inv.expiresAt)}</td>
                    <td><InviteStatusBadge invitation={inv} /></td>
                    <td>
                      {canRevoke ? (
                        <button
                          className="iam-btn-icon iam-btn-icon--danger"
                          title="Revoke"
                          disabled={removingId === inv.id}
                          onClick={() => void handleRevokeInvitation(inv)}
                        >
                          <span className="material-symbols-rounded">cancel</span>
                        </button>
                      ) : null}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  )
}
